/**
 * Owner: Masa Bayu
 * File: spsu-fonts-asset.ts
 * Date:Time: 2026-10-24 00:00
 * Description: D2 pecahan modul · kategori aset fon SPSU · senarai · FontReference ·
 *   pendaftaran FontFace. Badan identik kedua-dua salinan (pakej ≡ profil).
 */

import type { AssetLibrary, AssetReference, FontAssetInfo } from '../extract/spsu-types';

/** Font assets only · type === 'font' with measured font meta. */
export function listFontAssets(assets: AssetLibrary): readonly AssetReference[] {
  return Object.values(assets).filter(
    (asset) => asset.type === 'font' && asset.font !== undefined && asset.src.trim().length > 0,
  );
}

export function matchesFontQuery(info: FontAssetInfo, query: string): boolean {
  if (info.familyName === query) return true;
  if (info.sourceFontName === query) return true;
  return (info.aliases ?? []).includes(query);
}

/**
 * Register every SPSU font asset in document.fonts (browser).
 * Idempotent · skips families already loaded.
 * One bad FontFile2 (browser rejects) must not block later families —
 * e.g. Cambria EBDT fail must not drop SEAGARDENS (section titles lose their face).
 */
export async function loadAssetFonts(assets: AssetLibrary): Promise<readonly string[]> {
  if (typeof document === 'undefined' || !('fonts' in document)) return [];
  const loaded: string[] = [];
  for (const asset of listFontAssets(assets)) {
    const info = asset.font!;
    const weight = info.weight ?? '400';
    const already = [...document.fonts].some(
      (face) => face.family === info.familyName && String(face.weight) === weight,
    );
    if (already) {
      loaded.push(info.familyName);
      continue;
    }
    try {
      const face = new FontFace(info.familyName, `url(${asset.src})`, {
        weight,
        style: 'normal',
      });
      await face.load();
      document.fonts.add(face);
      loaded.push(info.familyName);
    } catch {
      // Measured subset may be browser-invalid (bitmap-only / truncated) · skip.
    }
  }
  return loaded;
}

/** Build a font AssetReference from measured PDF FontFile bytes metadata. */
export function fontAssetReference(input: {
  readonly id: string;
  readonly src: string;
  readonly format: 'ttf' | 'woff2';
  readonly font: FontAssetInfo;
  readonly checksum?: string;
}): AssetReference {
  return {
    id: input.id,
    type: 'font',
    src: input.src,
    format: input.format,
    font: input.font,
    ...(input.checksum ? { checksum: input.checksum } : {}),
  };
}
